import { z } from "zod";

export interface AutomationSetting {
  id: string;
  tenantId: string;
  settingKey: string;
  settingValue: unknown | null;
  isActive: boolean | null;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export const quoteFollowupSettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    delay: z.number().min(0, "Delay cannot be negative"),
    unit: z.enum(["days", "weeks"]),
  }),
  templates: z.object({
    email: z.string(),
    sms: z.string().optional(),
  }),
  limits: z
    .object({
      maxFollowups: z.number().min(1).optional(),
    })
    .optional(),
});

export type QuoteFollowupSetting = z.infer<typeof quoteFollowupSettingSchema>;

export const quoteExpirySettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    delay: z.number().min(1),
    unit: z.enum(["days"]),
  }),
  templates: z.object({
    email: z.string(),
    sms: z.string().optional(),
  }),
});

export type QuoteExpirySetting = z.infer<typeof quoteExpirySettingSchema>;

export const invoiceReminderSettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    delay: z.number().min(1),
    unit: z.enum(["days"]),
  }),
  templates: z.object({
    first: z.string(),
    escalated: z.string(),
  }),
  options: z.object({
    includePaymentLink: z.boolean(),
  }),
});

export type InvoiceReminderSetting = z.infer<typeof invoiceReminderSettingSchema>;

export const reviewRequestSettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    delay: z.number().min(1),
    unit: z.enum(["hours", "days"]),
  }),
  templates: z.object({
    email: z.string(),
    sms: z.string().optional(),
  }),
  options: z
    .object({})
    .optional(),
});

export type ReviewRequestSetting = z.infer<typeof reviewRequestSettingSchema>;

export const jobCompletionSettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    delay: z.number().min(1),
    unit: z.enum(["hours"]),
  }),
  templates: z.object({
    email: z.string(),
    sms: z.string().optional(),
  }),
});

export type JobCompletionSetting = z.infer<typeof jobCompletionSettingSchema>;

export const dailySummarySettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    sendTime: z.string(),
  }),
  options: z.object({
    recipients: z.array(z.string()),
    includeItems: z.object({
      jobsCompleted: z.boolean(),
      jobsOverSchedule: z.boolean(),
      paymentsReceived: z.boolean(),
      outstandingInvoices: z.boolean(),
      newQuoteRequests: z.boolean(),
    }),
  }),
});

export type DailySummarySetting = z.infer<typeof dailySummarySettingSchema>;

export const jobReminderSettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    delay: z.number().min(1),
    unit: z.enum(["days"]),
    sendTime: z.string(),
  }),
  templates: z.object({
    email: z.string(),
    sms: z.string().optional(),
  }),
  options: z.object({
    includeJobDetails: z.boolean(),
    notifyCustomer: z.boolean(),
    notifyStaff: z.boolean(),
  }),
});

export type JobReminderSetting = z.infer<typeof jobReminderSettingSchema>;

export const jobMorningOfSettingSchema = z.object({
  enabled: z.boolean(),
  schedule: z.object({
    sendHour: z.number().optional(),
    sendTime: z.string().optional(),
    timezone: z.string().optional(),
  }),
  channels: z.object({
    email: z.boolean(),
    sms: z.boolean(),
  }),
  templates: z.object({
    email: z.string(),
    sms: z.string().optional(),
  }),
  options: z
    .object({
      includeStaffName: z.boolean(),
    })
    .optional(),
});

export type JobMorningOfSetting = z.infer<typeof jobMorningOfSettingSchema>;
